import type {
  FerretPopupAddonConfig,
  FerretPopupVariantConfig,
  FerretToyName,
} from "../vts-config";
import {
  defaultPetBottomRange,
  getPetBounds,
  getPetBottomRange,
  getPetDepth,
  getPetLayer,
  showPetAlert,
} from "./pet-layer";

type SpriteAtlas = {
  frames: Record<string, AtlasFrame>;
  meta?: {
    size?: {
      w: number;
      h: number;
    };
  };
};

type AtlasFrame = {
  frame: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  duration?: number;
};

type SequenceFrame = AtlasFrame["frame"] & {
  duration: number;
};

type AnimationSequences = Record<string, SequenceFrame[]>;
type AnimationCycleStep = {
  name: string;
  repeats: number;
};
type FerretPopupPlacement = {
  left: number;
  bottom: number;
  facing: -1 | 1;
  area: FerretPopupArea;
};
type FerretPopupArea = {
  left: number;
  right: number;
  top: number;
  bottom: number;
};

type ToyAtlas = {
  toys: {
    image: string;
    size: {
      w: number;
      h: number;
    };
    sprites: Record<string, ToyClip>;
  };
};

type ToyClip = {
  x: number;
  y: number;
  w: number;
  h: number;
  frames: number;
  frameMs: number;
};

const defaultConfig: Required<
  Pick<
    FerretPopupAddonConfig,
    | "atlas"
    | "scale"
    | "minDelayMs"
    | "maxDelayMs"
    | "minAnimations"
    | "maxAnimations"
    | "horizontalPaddingPx"
    | "bottomRangePx"
    | "calmAnimations"
    | "calmAnimationRepeats"
    | "animations"
    | "runSpeedPxPerSecond"
    | "toyAtlas"
    | "toyChance"
    | "toys"
  >
> = {
  atlas: "/addons/ferrets/ferret-sprite-sheet.json",
  scale: 3,
  minDelayMs: 8_000,
  maxDelayMs: 18_000,
  minAnimations: 2,
  maxAnimations: 5,
  horizontalPaddingPx: 96,
  bottomRangePx: defaultPetBottomRange,
  calmAnimations: ["Idle", "Idle2", "Sleep"],
  calmAnimationRepeats: [2, 4],
  animations: ["Jump", "Movement"],
  runSpeedPxPerSecond: 150,
  toyAtlas: "/addons/cats/cat-sprite-sheet.json",
  toyChance: 0.3,
  toys: ["ball", "mouse"],
};

const ballSprites = ["ball-blue", "ball-orange", "ball-pink"];
// The mouse chase is long, so it comes up less often than the ball.
const toyWeights: Record<FerretToyName, number> = {
  ball: 4,
  mouse: 1,
};
// Transparent rows below the mouse's feet in its toy frames.
const mouseFootGapPx = 3;
// How quickly a kicked ball slows down, in px/s^2.
const ballFriction = 160;
// How close a mouse sneaks up before the ferret notices it.
const mouseNoticeDistancePx = 190;

const defaultFrameSize = 43;
const defaultAtlasSize = { w: 344, h: 387 };

const defaultVariants: FerretPopupVariantConfig[] = [
  {
    id: "milk",
    label: "Milk",
    sprite: "/addons/ferrets/milk.png",
    minDelayMs: 7_000,
    maxDelayMs: 16_000,
  },
  {
    id: "coffee",
    label: "Coffee",
    sprite: "/addons/ferrets/coffee.png",
    minDelayMs: 10_000,
    maxDelayMs: 22_000,
  },
];

let activeAddon: FerretPopupAddon | null = null;

export function initializeFerretPopups(
  config: FerretPopupAddonConfig | undefined,
): void {
  if (!config?.enabled || activeAddon) {
    return;
  }

  activeAddon = new FerretPopupAddon(config);
  void activeAddon.start();
}

class FerretPopupAddon {
  private readonly config: FerretPopupAddonConfig;
  private readonly layer = getPetLayer();
  private readonly occupiedAreas = new Map<string, FerretPopupArea>();
  private toyAtlas: ToyAtlas | null = null;
  private toyImage = "";

  public constructor(config: FerretPopupAddonConfig) {
    this.config = config;
  }

  public async start(): Promise<void> {
    const atlasUrl = this.config.atlas ?? defaultConfig.atlas;
    const atlas = await fetchJson<SpriteAtlas>(atlasUrl);
    const sequences = readSequences(atlas);
    const atlasSize = atlas.meta?.size ?? defaultAtlasSize;
    const variants = this.config.variants?.length
      ? this.config.variants
      : defaultVariants;

    await this.loadToys();
    variants.forEach((variant) => {
      new FerretPopupActor(
        variant,
        this.config,
        sequences,
        atlasSize,
        this,
      ).scheduleNext(true);
    });
  }

  public getLayer(): HTMLElement {
    return this.layer;
  }

  public getToys(): FerretToyName[] {
    return this.toyAtlas ? (this.config.toys ?? defaultConfig.toys) : [];
  }

  public getToyChance(): number {
    return this.config.toyChance ?? defaultConfig.toyChance;
  }

  public getRunSpeed(): number {
    return this.config.runSpeedPxPerSecond ?? defaultConfig.runSpeedPxPerSecond;
  }

  public getScale(): number {
    return this.config.scale ?? defaultConfig.scale;
  }

  public createToy(name: string): FerretToy | null {
    const clip = this.toyAtlas?.toys.sprites[name];
    if (!this.toyAtlas || !clip) {
      return null;
    }

    return new FerretToy(
      this.layer,
      this.toyImage,
      this.toyAtlas.toys.size,
      clip,
      this.getScale(),
    );
  }

  public getBounds(frameSize: number): [number, number] {
    const padding =
      this.config.horizontalPaddingPx ?? defaultConfig.horizontalPaddingPx;
    return getPetBounds(frameSize, padding);
  }

  public updatePlacement(
    id: string,
    left: number,
    bottom: number,
    frameSize: number,
  ): void {
    this.occupiedAreas.set(id, createArea(left, bottom, frameSize));
  }

  public reservePlacement(id: string, frameSize: number): FerretPopupPlacement {
    let bestPlacement = this.createPlacement(frameSize);
    let bestOverlap = this.measureOccupiedOverlap(bestPlacement.area);

    for (let attempt = 0; attempt < 32; attempt += 1) {
      const placement = this.createPlacement(frameSize);
      const overlap = this.measureOccupiedOverlap(placement.area);
      if (overlap === 0) {
        this.occupiedAreas.set(id, placement.area);
        return placement;
      }

      if (overlap < bestOverlap) {
        bestPlacement = placement;
        bestOverlap = overlap;
      }
    }

    this.occupiedAreas.set(id, bestPlacement.area);
    return bestPlacement;
  }

  public releasePlacement(id: string): void {
    this.occupiedAreas.delete(id);
  }

  private async loadToys(): Promise<void> {
    const toys = this.config.toys ?? defaultConfig.toys;
    if (toys.length === 0) {
      return;
    }

    const atlasUrl = this.config.toyAtlas ?? defaultConfig.toyAtlas;
    try {
      this.toyAtlas = await fetchJson<ToyAtlas>(atlasUrl);
      this.toyImage = new URL(
        this.toyAtlas.toys.image,
        new URL(atlasUrl, window.location.href),
      ).href;
    } catch {
      // Toys are optional; the ferrets still pop up without them.
      this.toyAtlas = null;
    }
  }

  private createPlacement(frameSize: number): FerretPopupPlacement {
    const [minLeft, maxLeft] = this.getBounds(frameSize);
    const [minBottom, maxBottom] = getPetBottomRange(
      this.config.bottomRangePx ?? defaultConfig.bottomRangePx,
      frameSize,
    );
    const left = randomBetween(minLeft, maxLeft);
    const bottom = randomBetween(minBottom, maxBottom);

    return {
      left,
      bottom,
      facing: Math.random() < 0.5 ? -1 : 1,
      area: createArea(left, bottom, frameSize),
    };
  }

  private measureOccupiedOverlap(area: FerretPopupArea): number {
    return Array.from(this.occupiedAreas.values()).reduce(
      (overlap, occupiedArea) => {
        return overlap + measureAreaOverlap(area, occupiedArea);
      },
      0,
    );
  }
}

class FerretPopupActor {
  private readonly element = document.createElement("div");
  private isActive = false;
  private timeoutId: number | undefined;
  private left = 0;
  private bottom = 0;

  public constructor(
    private readonly variant: FerretPopupVariantConfig,
    private readonly addonConfig: FerretPopupAddonConfig,
    private readonly sequences: AnimationSequences,
    private readonly atlasSize: { w: number; h: number },
    private readonly addon: FerretPopupAddon,
  ) {
    this.element.className = "ferret-popup";
    this.element.dataset.ferret = variant.id;
    this.element.setAttribute("role", "img");
    this.element.setAttribute("aria-label", variant.label ?? variant.id);
    this.element.style.backgroundImage = `url("${variant.sprite}")`;
    addon.getLayer().append(this.element);
  }

  public scheduleNext(initial = false): void {
    window.clearTimeout(this.timeoutId);
    const delay = initial
      ? randomBetween(1_000, this.maxDelayMs())
      : randomBetween(this.minDelayMs(), this.maxDelayMs());

    this.timeoutId = window.setTimeout(() => {
      void this.playVisit();
    }, delay);
  }

  private async playVisit(): Promise<void> {
    if (this.isActive || document.hidden) {
      this.scheduleNext();
      return;
    }

    this.isActive = true;
    try {
      this.place();
      this.element.classList.add("is-visible");

      await this.playSequence("Emerge");
      await this.playRandomMiddleAnimations();
      await this.playWithToy();
      await this.playSequence("Dig");
      await this.playSequence("Disappear");
    } finally {
      this.element.classList.remove("is-visible");
      this.addon.releasePlacement(this.variant.id);
      this.isActive = false;
      this.scheduleNext();
    }
  }

  private async playRandomMiddleAnimations(): Promise<void> {
    const minAnimations =
      this.addonConfig.minAnimations ?? defaultConfig.minAnimations;
    const maxAnimations =
      this.addonConfig.maxAnimations ?? defaultConfig.maxAnimations;
    const count = randomBetween(minAnimations, Math.max(minAnimations, maxAnimations));
    const sequence = this.createMiddleAnimationCycle(count);

    for (const step of sequence) {
      if (step.name === "Movement") {
        await this.scurryTo(this.pickNearbyLeft(120, 320));
        continue;
      }

      for (let repeat = 0; repeat < step.repeats; repeat += 1) {
        await this.playSequence(step.name);
      }
    }
  }

  private async playWithToy(): Promise<void> {
    const toys = this.addon.getToys();
    if (toys.length === 0 || Math.random() >= this.addon.getToyChance()) {
      return;
    }

    if (pickWeighted(toys.map((toy) => [toy, toyWeights[toy]])) === "mouse") {
      await this.chaseMouse();
    } else {
      await this.playWithBall();
    }
    await this.playSequence("Idle");
  }

  private async playWithBall(): Promise<void> {
    const ball = this.addon.createToy(pickRandom(ballSprites));
    if (!ball) {
      return;
    }

    const reach = this.frameSize() * 0.45;
    ball.place(this.pickNearbyLeft(140, 300), this.bottom, this.bottom);
    ball.setVisible(true);

    try {
      for (let pounce = randomBetween(1, 3); pounce > 0; pounce -= 1) {
        await this.scurryTo(
          ball.left + (ball.left > this.left ? -reach : reach),
        );
        this.setFacing(ball.left > this.left ? 1 : -1);

        const kick = ball.slideTo(
          this.pickNearbyLeft(140, 280),
          ballFriction,
        );
        await this.playSequence("Jump");
        await kick;
      }
    } finally {
      await ball.remove();
    }
  }

  private async chaseMouse(): Promise<void> {
    const mouse = this.addon.createToy("mouse");
    if (!mouse) {
      return;
    }

    const offscreen = this.frameSize();
    const speed = this.addon.getRunSpeed();
    const [minLeft, maxLeft] = this.addon.getBounds(this.frameSize());
    // The mouse comes from the far side, so there is room to chase it back.
    const fromLeft = this.left - minLeft > maxLeft - this.left;
    const edgeLeft = fromLeft ? -offscreen : window.innerWidth + offscreen;

    mouse.place(
      edgeLeft,
      this.bottom - mouseFootGapPx * this.addon.getScale(),
      this.bottom,
    );
    mouse.setVisible(true);

    try {
      let hasArrived = false;
      void mouse
        .moveTo(
          this.left + (fromLeft ? -1 : 1) * mouseNoticeDistancePx,
          speed * 0.7,
        )
        .then(() => {
          hasArrived = true;
          mouse.setPaused(true);
        });
      while (!hasArrived) {
        await this.playSequence("Idle");
      }

      // Both freeze for a beat when the ferret spots it.
      const facing = fromLeft ? -1 : 1;
      const scale = this.addon.getScale();
      this.setFacing(facing);
      showPetAlert(
        this.left + facing * 12 * scale,
        this.bottom + 19 * scale,
        this.bottom,
        scale,
      );
      this.element.style.setProperty("--ferret-lift", `${3 * scale}px`);
      await wait(140);
      this.element.style.setProperty("--ferret-lift", "0px");
      await wait(610);

      mouse.setPaused(false);
      const escape = mouse.moveTo(edgeLeft, speed * 1.2);
      await this.scurryTo(fromLeft ? minLeft : maxLeft);
      await escape;
    } finally {
      await mouse.remove();
    }
  }

  private async scurryTo(targetLeft: number): Promise<void> {
    const frames = this.sequences.Movement ?? [];
    if (frames.length === 0) {
      return;
    }

    if (targetLeft !== this.left) {
      this.setFacing(targetLeft > this.left ? 1 : -1);
    }

    for (let index = 0; Math.abs(targetLeft - this.left) > 0.5; index += 1) {
      const frame = frames[index % frames.length];
      const distance = targetLeft - this.left;
      const step = Math.min(
        Math.abs(distance),
        (this.addon.getRunSpeed() * frame.duration) / 1000,
      );

      this.left += Math.sign(distance) * step;
      this.element.style.left = `${this.left}px`;
      this.showFrame(frame);
      await wait(frame.duration);
    }

    this.addon.updatePlacement(
      this.variant.id,
      this.left,
      this.bottom,
      this.frameSize(),
    );
  }

  private pickNearbyLeft(minDistance: number, maxDistance: number): number {
    const [minLeft, maxLeft] = this.addon.getBounds(this.frameSize());
    const distance = randomBetween(minDistance, maxDistance);
    const candidates = [this.left - distance, this.left + distance].filter(
      (left) => left >= minLeft && left <= maxLeft,
    );

    return candidates.length > 0
      ? pickRandom(candidates)
      : randomBetween(minLeft, maxLeft);
  }

  private setFacing(facing: -1 | 1): void {
    this.element.style.setProperty("--ferret-facing", String(facing));
  }

  private frameSize(): number {
    return (
      (this.sequences.Emerge?.[0]?.w ?? defaultFrameSize) * this.addon.getScale()
    );
  }

  private createMiddleAnimationCycle(count: number): AnimationCycleStep[] {
    const calmAnimations = this.addonConfig.calmAnimations?.length
      ? this.addonConfig.calmAnimations
      : defaultConfig.calmAnimations;
    const activeAnimations = this.addonConfig.animations?.length
      ? this.addonConfig.animations
      : defaultConfig.animations;
    const calmAnimationRepeats =
      this.addonConfig.calmAnimationRepeats ??
      defaultConfig.calmAnimationRepeats;
    const calmCount = Math.max(1, Math.ceil(count / 2));
    const activeCount = Math.max(0, count - calmCount);
    const sequence: AnimationCycleStep[] = [];

    for (let index = 0; index < calmCount; index += 1) {
      sequence.push({
        name: pickRandom(calmAnimations),
        repeats: randomBetween(calmAnimationRepeats[0], calmAnimationRepeats[1]),
      });
    }

    for (let index = 0; index < activeCount; index += 1) {
      sequence.push({
        name: pickRandom(activeAnimations),
        repeats: 1,
      });
    }

    return shuffle(sequence);
  }

  private async playSequence(name: string): Promise<void> {
    const frames = this.sequences[name] ?? [];
    for (const frame of frames) {
      this.showFrame(frame);
      await wait(frame.duration);
    }
  }

  private showFrame(frame: SequenceFrame): void {
    const scale = this.addonConfig.scale ?? defaultConfig.scale;
    this.element.style.width = `${frame.w * scale}px`;
    this.element.style.height = `${frame.h * scale}px`;
    this.element.style.backgroundSize = `${this.atlasSize.w * scale}px ${
      this.atlasSize.h * scale
    }px`;
    this.element.style.backgroundPosition = `-${frame.x * scale}px -${
      frame.y * scale
    }px`;
  }

  private place(): void {
    const placement = this.addon.reservePlacement(
      this.variant.id,
      this.frameSize(),
    );

    this.left = placement.left;
    this.bottom = placement.bottom;
    this.element.style.left = `${placement.left}px`;
    this.element.style.bottom = `${placement.bottom}px`;
    this.element.style.zIndex = getPetDepth(placement.bottom);
    this.setFacing(placement.facing);
  }

  private minDelayMs(): number {
    return (
      this.variant.minDelayMs ??
      this.addonConfig.minDelayMs ??
      defaultConfig.minDelayMs
    );
  }

  private maxDelayMs(): number {
    return (
      this.variant.maxDelayMs ??
      this.addonConfig.maxDelayMs ??
      defaultConfig.maxDelayMs
    );
  }
}

class FerretToy {
  public left = 0;
  private readonly element = document.createElement("div");
  private readonly frameTimer: number | undefined;
  private isPaused = false;

  public constructor(
    layer: HTMLElement,
    image: string,
    sheetSize: { w: number; h: number },
    clip: ToyClip,
    scale: number,
  ) {
    const style = this.element.style;
    this.element.className = "ferret-toy";
    style.width = `${clip.w * scale}px`;
    style.height = `${clip.h * scale}px`;
    style.backgroundImage = `url("${image}")`;
    style.backgroundSize = `${sheetSize.w * scale}px ${sheetSize.h * scale}px`;

    let frameIndex = 0;
    const showFrame = (): void => {
      if (this.isPaused) {
        return;
      }

      style.backgroundPosition = `-${(clip.x + frameIndex * clip.w) * scale}px -${
        clip.y * scale
      }px`;
      frameIndex = (frameIndex + 1) % clip.frames;
    };
    showFrame();
    if (clip.frames > 1 && clip.frameMs > 0) {
      this.frameTimer = window.setInterval(showFrame, clip.frameMs);
    }

    layer.append(this.element);
  }

  // ownerBottom keeps the toy at its owner's depth.
  public place(left: number, bottom: number, ownerBottom: number): void {
    this.left = left;
    this.element.style.left = `${left}px`;
    this.element.style.bottom = `${bottom}px`;
    this.element.style.zIndex = getPetDepth(ownerBottom);
  }

  public setPaused(isPaused: boolean): void {
    this.isPaused = isPaused;
  }

  public setVisible(isVisible: boolean): void {
    // Read layout first so the fade-in starts from the hidden state.
    void this.element.offsetWidth;
    this.element.classList.toggle("is-visible", isVisible);
  }

  public moveTo(left: number, speed: number): Promise<void> {
    return this.move(
      left,
      (Math.abs(left - this.left) / speed) * 1000,
      "linear",
    );
  }

  // Slides to left under constant friction (px/s^2): a longer slide starts
  // faster, and every slide slows down at the same rate.
  public slideTo(left: number, friction: number): Promise<void> {
    // The curve is an exact quadratic ease-out, i.e. constant deceleration.
    return this.move(
      left,
      Math.sqrt((2 * Math.abs(left - this.left)) / friction) * 1000,
      "cubic-bezier(0.333, 0.667, 0.667, 1)",
    );
  }

  private async move(
    left: number,
    duration: number,
    easing: string,
  ): Promise<void> {
    this.element.style.setProperty("--ferret-toy-move-ms", `${duration}ms`);
    this.element.style.setProperty("--ferret-toy-move-ease", easing);
    this.element.style.setProperty(
      "--ferret-facing",
      left < this.left ? "-1" : "1",
    );
    this.left = left;
    this.element.style.left = `${left}px`;
    await wait(duration);
  }

  public async remove(): Promise<void> {
    this.setVisible(false);
    await wait(350);
    window.clearInterval(this.frameTimer);
    this.element.remove();
  }
}

function createArea(
  left: number,
  bottom: number,
  frameSize: number,
): FerretPopupArea {
  const margin = 16;

  return {
    left: left - frameSize / 2 - margin,
    right: left + frameSize / 2 + margin,
    top: window.innerHeight - bottom - frameSize - margin,
    bottom: window.innerHeight - bottom + margin,
  };
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Unable to load ferret popup asset ${url}: ${response.status}`);
  }

  return (await response.json()) as T;
}

function readSequences(atlas: SpriteAtlas): AnimationSequences {
  const sequences: AnimationSequences = {};
  const framePattern = /\(([^)]+)\)\s+(\d+)\.ase$/;

  Object.entries(atlas.frames).forEach(([name, frame]) => {
    const match = framePattern.exec(name);
    if (!match) {
      return;
    }

    const sequenceName = match[1];
    const sequenceIndex = Number.parseInt(match[2], 10);
    const sequence = (sequences[sequenceName] ??= []);
    sequence[sequenceIndex] = {
      ...frame.frame,
      duration: frame.duration ?? 100,
    };
  });

  Object.keys(sequences).forEach((name) => {
    sequences[name] = sequences[name].filter(Boolean);
  });

  return sequences;
}

function pickRandom<T>(items: T[]): T {
  return items[randomBetween(0, items.length - 1)];
}

function pickWeighted<T>(items: Array<[T, number]>): T {
  let roll = Math.random() * items.reduce((total, [, weight]) => total + weight, 0);
  for (const [item, weight] of items) {
    roll -= weight;
    if (roll < 0) {
      return item;
    }
  }

  return items[items.length - 1][0];
}

function shuffle<T>(items: T[]): T[] {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = randomBetween(0, index);
    [shuffled[index], shuffled[swapIndex]] = [
      shuffled[swapIndex],
      shuffled[index],
    ];
  }

  return shuffled;
}

function measureAreaOverlap(
  firstArea: FerretPopupArea,
  secondArea: FerretPopupArea,
): number {
  const width = Math.max(
    0,
    Math.min(firstArea.right, secondArea.right) -
      Math.max(firstArea.left, secondArea.left),
  );
  const height = Math.max(
    0,
    Math.min(firstArea.bottom, secondArea.bottom) -
      Math.max(firstArea.top, secondArea.top),
  );

  return width * height;
}

function randomBetween(min: number, max: number): number {
  if (max <= min) {
    return Math.round(min);
  }

  return Math.round(min + Math.random() * (max - min));
}

function wait(duration: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, duration));
}
